import { useEffect, useMemo, useState } from "react"

import { SEO } from "@/components/SEO"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { getApiBaseUrl, readApiJson } from "@/lib/api"

type TenantApi = {
  id: number
  status?: string | null
  createdAt?: string
  created_at?: string
}

function isActiveTenant(status?: string | null) {
  return (status || "active").toLowerCase() === "active"
}

export function DashboardPage() {
  const API_BASE = getApiBaseUrl()

  const [tenants, setTenants] = useState<TenantApi[]>([])
  const [totalClients, setTotalClients] = useState(0)
  const [loading, setLoading] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function loadDashboard() {
      setLoading(true)
      setApiError(null)

      try {
        const res = await fetch(`${API_BASE}/tenants?page=1&limit=1000`)
        const json = await readApiJson(res, "Failed to load dashboard data.")

        const data: TenantApi[] = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : []
        const metaTotal = Number(json?.meta?.total)

        if (!cancelled) {
          setTenants(data)
          setTotalClients(Number.isFinite(metaTotal) ? metaTotal : data.length)
        }
      } catch (e: any) {
        if (!cancelled) {
          setTenants([])
          setTotalClients(0)
          setApiError(e?.message || "Failed to load dashboard data.")
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    loadDashboard()

    return () => {
      cancelled = true
    }
  }, [API_BASE])

  const metrics = useMemo(() => {
    const activeClients = tenants.filter((tenant) => isActiveTenant(tenant.status)).length
    const inactiveClients = Math.max(totalClients - activeClients, 0)

    return [
      {
        title: "Total clients",
        value: totalClients,
        helper: "All tenants in the platform",
      },
      {
        title: "Active clients",
        value: activeClients,
        helper: "Clients marked active",
      },
      {
        title: "Attention needed",
        value: inactiveClients,
        helper: "Trial or suspended clients",
      },
    ]
  }, [tenants, totalClients])

  return (
    <>
      <SEO title="Dashboard" description="Internal dashboard overview." />

      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Overview of clients, activity, and platform health.
          </p>
        </div>

        {apiError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {apiError}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {metrics.map((metric) => (
            <Card key={metric.title}>
              <CardHeader>
                <CardTitle>{metric.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-semibold">
                  {loading ? "Loading..." : metric.value.toLocaleString()}
                </div>
                <div className="text-sm text-muted-foreground mt-1">{metric.helper}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </>
  )
}

